import { Form, Formik } from 'formik';
import {
  DialogBody,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogClose,
} from '@src/design-system/dialog';
import { Button } from '@src/design-system/button';

import { COLUMN_TYPES } from '../constants/column-types';

import {
  createCustomFieldsInitialValues,
  customFieldsFormSchema,
  type TCustomFieldsFormValues,
} from '../custom-field-form';

import { Textfield } from '@src/shared/fields/text-field';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@src/design-system/dropdown-menu';
import { inputVariants } from '@src/design-system/input';
import { cn } from '@src/design-system/helpers';
import { formatTextForDisplay } from '@src/shared/utils/helpers/format-text-for-display';
import { ChevronDown } from 'lucide-react';
import { Label } from '@src/design-system/label';
import { CustomFieldOptionsManager } from './custom-field-options-manager';
import customFieldApi from '@src/shared/features/custom-fields/services/custom-field-api';

import { useRef } from 'react';
import { useTiclickTableContext } from '@src/design-system/molecules/ticlick-table/context/ticlick-table-context-hook';
import type { TCustomField } from '@src/shared/types/custom-field';

export const CustomFieldAddDialog = ({
  customField,
}: {
  customField?: TCustomField;
}) => {
  const { mutate: createCustomField, isPending: isCreating } =
    customFieldApi.useCreate();
  const { mutate: updateCustomField, isPending: isUpdating } =
    customFieldApi.useUpdate();

  const { table } = useTiclickTableContext();

  const closeRef = useRef<HTMLButtonElement>(null);

  const handleSubmit = (values: TCustomFieldsFormValues) => {
    const entity = table.options.meta?.identifyingData.customFieldEntityType;

    if (!entity) return;

    const onSuccess = () => {
      closeRef.current?.click();
    };

    if (customField) {
      updateCustomField(
        {
          id: customField.id,
          label: values.name,
          name: values.name,
          type: values.type.value,
          options: values.options,
          entity,
        },
        {
          onSuccess,
        }
      );
    } else {
      createCustomField(
        {
          name: values.name,
          label: values.name,
          type: values.type.value,
          options: values.options.map((option) => option.trim()),
          entity,
        },
        {
          onSuccess,
        }
      );
    }
  };

  const dialogTitle = customField ? 'Edit Column' : 'Add Column';

  return (
    <Formik
      initialValues={createCustomFieldsInitialValues(customField)}
      validationSchema={customFieldsFormSchema}
      onSubmit={handleSubmit}
    >
      {({ values, setFieldValue, dirty }) => (
        <Form className='flex flex-col h-full gap-4'>
          <DialogHeader>
            <DialogTitle>{dialogTitle}</DialogTitle>
          </DialogHeader>

          <DialogBody className='flex flex-col gap-6'>
            <Textfield
              className='text-sm'
              name='name'
              label='Column Name'
              placeholder='Enter column name'
            />

            <div>
              <Label>Column Type</Label>
              <DropdownMenu>
                <DropdownMenuTrigger
                  disabled={!!customField}
                  className={cn(
                    'cursor-pointer w-full flex items-center justify-between text-sm',
                    inputVariants()
                  )}
                >
                  <div className='flex items-center gap-2'>
                    <values.type.icon className='size-4' />
                    {formatTextForDisplay(values.type.label)}
                  </div>
                  <ChevronDown className='size-4' />
                </DropdownMenuTrigger>

                <DropdownMenuContent>
                  {COLUMN_TYPES.map((type) => {
                    const isSelected = values.type.value === type.value;
                    return (
                      <DropdownMenuItem
                        key={type.value}
                        onClick={() => setFieldValue('type', type)}
                        className={cn(
                          isSelected &&
                            'bg-primary-50 text-primary-900 hover:bg-primary-50'
                        )}
                      >
                        <type.icon className='size-4' />
                        {type.label}
                      </DropdownMenuItem>
                    );
                  })}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            <CustomFieldOptionsManager canRemoveOptions={!customField} />
          </DialogBody>

          <DialogFooter>
            <Button
              isLoading={isCreating || isUpdating}
              disabled={!dirty}
              type='submit'
              className='w-full'
            >
              Confirm
            </Button>

            <DialogClose
              ref={closeRef}
              className='hidden'
            />
          </DialogFooter>
        </Form>
      )}
    </Formik>
  );
};
